import React, { useState, useEffect } from 'react';

import {
  BackDrop,
  Modal,
  ModalBody,
  ModalTitle,
  ModalContent,
  ModalFooter,
  AddressFormGroup,
  CityStateZipRow,
  CardContent,
  ModalHeader,
  ModalSubtitle,
} from './VerifyNewAddressModal.styles';
import { Button } from '@/components/Button/DefaultButton/Button';
import { AddressAutoComplete } from '@/components/input/AddressAutoComplete/AddressAutoComplete';
import { TextInput } from '@/components/input/TextInput/TextInput';
import { DropDownInput } from '@/components/input/DropDownInput/DropDownInput';
import { CheckConsent } from '@/components/input/CheckConsent/CheckConsent';
import { FormGroupComponent } from '@/components/input/FormGroup/FormGroup';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { TypeOfText, TextBlock } from '@/components/text/TextBlock';
import styled from 'styled-components';

const AddressInUseButtons = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
`;

interface VerifyNewAddressModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (formValues: typeof initialFormValues, isMailingAddressSame: boolean) => void;
  inUseAddresses: string[];
}

const initialFormValues = {
  address: '',
  suit: '',
  city: '',
  state: '',
  zip: '',
};

const VerifyNewAddressModal: React.FC<VerifyNewAddressModalProps> = ({ open, onClose, onContinue, inUseAddresses }) => {
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const [isMailingAddressSame, setIsMailingAddressSame] = useState(true);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [addressInUse, setAddressInUse] = useState(false);
  const dispatch = useAppDispatch();
  const borrowerProperty = useAppSelector((state) => state.LoanApplication.borrowerProperty || {});

  useEffect(() => {
    const { propertyStreetAddress, propertyCity, propertyState, propertyZip } = borrowerProperty;
    if (!addressInUse && propertyStreetAddress) {
      setFormValues((prev) => ({
        ...prev,
        address: propertyStreetAddress,
        city: propertyCity || prev.city,
        state: propertyState || prev.state,
        zip: propertyZip || prev.zip,
      }));
    }
  }, [borrowerProperty, addressInUse]);

  const isAddressInUse = (address: string, city: string, state: string, zip: string): boolean => {
    const fullAddress = [address, city, state, zip].filter(Boolean).join(', ').toLowerCase();
    const newCityStateZip = [city, state, zip].filter(Boolean).join(', ').toLowerCase();

    const isInUse = inUseAddresses.some((addr) => {
      const exactMatch = addr === fullAddress;
      if (exactMatch) {
        return true;
      }

      const existingParts = addr.split(', ');
      const newParts = fullAddress.split(', ');

      if (existingParts.length >= 3 && newParts.length >= 3) {
        const existingCityStateZip = existingParts.slice(-3).join(', ');
        const newAddressCityStateZip = newParts.slice(-3).join(', ');

        if (existingCityStateZip === newAddressCityStateZip) {
          return true;
        }
      }

      const addressEndsWithCityStateZip = addr.endsWith(newCityStateZip);
      if (addressEndsWithCityStateZip) {
        return true;
      }

      return false;
    });

    return isInUse;
  };

  const handleChange = (value: string | number | boolean | React.SyntheticEvent, name: string) => {
    const actualValue = value && typeof value === 'object' && 'target' in value ? (value.target as HTMLInputElement).value : value;

    if (!name) {
      console.warn('handleChange called without a name parameter');
      return;
    }

    if (['address', 'city', 'state', 'zip'].includes(name)) {
      setAddressInUse(false);
    }

    setFormValues((prev) => {
      const updatedValues = { ...prev, [name]: actualValue };

      if (['address', 'city', 'state', 'zip'].includes(name)) {
        dispatch({
          type: 'LoanApplication/updateValue',
          payload: {
            ...loanApplication,
            borrowerProperty: {
              propertyStreetAddress: updatedValues.address,
              propertyCity: updatedValues.city,
              propertyState: updatedValues.state,
              propertyZip: updatedValues.zip,
              propertyCountry: 'United States',
            },
          },
        });
      }

      return updatedValues;
    });
  };

  const handleMailingAddressCheckbox = (checked: boolean) => {
    setIsMailingAddressSame(checked);
  };

  const handleTryAnotherAddress = () => {
    setAddressInUse(false);
    setFormValues({
      address: '',
      suit: '',
      city: '',
      state: '',
      zip: '',
    });
    dispatch({
      type: 'LoanApplication/updateValue',
      payload: {
        ...loanApplication,
        borrowerProperty: {
          propertyStreetAddress: '',
          propertyCity: '',
          propertyState: '',
          propertyZip: '',
          propertyCountry: 'United States',
        },
      },
    });
  };

  const handleClose = () => {
    window.location.href = templateConfig?.pages?.returnPage || 'https://www.firstclose.com';
  };

  const handleContinue = () => {
    const isInUse = isAddressInUse(formValues.address, formValues.city, formValues.state, formValues.zip);

    if (isInUse) {
      setAddressInUse(true);
    } else {
      try {
        onContinue(formValues, isMailingAddressSame);
      } catch (error) {
        console.error('Error calling onContinue:', error);
      }
    }
  };

  return (
    <>
      <BackDrop $isOpen={open} data-testid="modal-backdrop" onClick={onClose} />
      <Modal $isOpen={open}>
        {!addressInUse ? (
          <ModalBody>
            <ModalTitle>
              <TextBlock text="Verify New Address" direction="center" color="textPrimary" type={TypeOfText.H2} />
            </ModalTitle>
            <ModalContent>
              <TextBlock
                textAlign="center"
                text="To start an application with a new property please enter the new address below."
                direction="center"
                color="default"
                type={TypeOfText.Subtitle}
              />
              <FormGroupComponent onChange={handleChange}>
                <CardContent>
                  <ModalHeader>New Property Address</ModalHeader>
                  <AddressFormGroup>
                    <AddressAutoComplete size="100%" onChange={(value) => handleChange(value, 'address')} typeOfIcon="address" propertyType="address" value={formValues.address} />
                    <CityStateZipRow>
                      <TextInput propertyType="city" placeholder="City" value={formValues.city} dataTestId="city" onChange={(value) => handleChange(value, 'city')} />
                      <DropDownInput
                        propertyType="state"
                        placeHolder="State"
                        options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.options}
                        value={formValues.state}
                        dataTestId="state"
                        onChange={(value) => handleChange(value, 'state')}
                        capitalizeWord={false}
                      />
                      <TextInput propertyType="zip" value={formValues.zip} placeholder="Zip code" dataTestId="zip" onChange={(value) => handleChange(value, 'zip')} />
                    </CityStateZipRow>
                    <TextInput
                      propertyType="suit"
                      placeholder="Unit /suite"
                      dataTestId="suit"
                      value={formValues.suit}
                      onChange={(value) => handleChange(value, 'suit')}
                      isRequired={false}
                    />
                  </AddressFormGroup>
                  <CheckConsent
                    type="mailing"
                    text="This property is also my mailing address."
                    direction="center"
                    checked={isMailingAddressSame}
                    onChange={handleMailingAddressCheckbox}
                    isHidden={false}
                  />
                </CardContent>
              </FormGroupComponent>
            </ModalContent>
            <ModalFooter>
              <Button label="Back" variant="secondary" buttonSize="s" onClick={onClose} />
              <Button label="Continue" variant="primary" buttonSize="s" onClick={handleContinue} isDisabled={addressInUse} />
            </ModalFooter>
          </ModalBody>
        ) : (
          <ModalBody>
            <ModalTitle>
              <TextBlock text="Address Already in Use" direction="center" color="textPrimary" type={TypeOfText.H2} />
            </ModalTitle>
            <ModalSubtitle>
              To start a new application, you'll need to enter a different address. Please try again with an address not linked to an existing application.
            </ModalSubtitle>
            <AddressInUseButtons>
              <Button label="Try Another Address" variant="secondary" buttonSize="s" onClick={handleTryAnotherAddress} />
              <Button label="Close" variant="primary" buttonSize="s" onClick={handleClose} />
            </AddressInUseButtons>
          </ModalBody>
        )}
      </Modal>
    </>
  );
};

export default VerifyNewAddressModal;
